/**
 * Unfilled roster slot coverage clock. Operational, not a legislated
 * compliance clock — TARGET, included in the shared library because the
 * cron already reminds on it and a future worklist should not re-derive
 * its own urgency rule.
 *
 * Applicability (is this slot actually actionable right now?) stays with
 * `resolveUnfilledRosterTaskSource` in
 * `apps/web/src/lib/workflow-orchestration/operational-task-source-facts.ts`
 * — this function only classifies urgency for a slot the caller has already
 * decided is actionable.
 */
import type { ComplianceDeadlineItem } from './types';
import unfilledAlertPolicy from '../unfilled-alert-policy.json';

// Canonical value also generates the pinned SQL successor; parity is tested.
export const UNFILLED_ALERT_HORIZON_HOURS = unfilledAlertPolicy.UNFILLED_ALERT_HORIZON_HOURS;

const MS_PER_HOUR = 1000 * 60 * 60;
const PROVISION = 'Statura operational target (roster coverage)';

export interface UnfilledRosterSlotRow {
  slotId: string;
  shiftId: string;
  startTime: string;
  serviceType: string | null;
}

export type UnfilledRosterSlotPriority = 'HIGH' | 'MEDIUM';

export function evaluateUnfilledRosterSlotUrgency(
  row: UnfilledRosterSlotRow,
  now: Date,
): { item: ComplianceDeadlineItem; priority: UnfilledRosterSlotPriority; hoursUntil: number } {
  const startMs = new Date(row.startTime).getTime();
  const hoursUntil = (startMs - now.getTime()) / MS_PER_HOUR;
  const priority: UnfilledRosterSlotPriority = hoursUntil <= 24 ? 'HIGH' : 'MEDIUM';
  return {
    priority,
    hoursUntil,
    item: {
      id: `roster-unfilled-slot:${row.slotId}`,
      register: 'ROSTER_COVERAGE',
      recordRef: row.slotId,
      title: 'Unfilled roster slot requires allocation',
      dueAt: row.startTime,
      kind: 'TARGET',
      provisionOrTargetLabel: PROVISION,
      status: priority === 'HIGH' ? 'DUE_SOON' : 'UPCOMING',
      href: `/roster/replace/${row.shiftId}?slotId=${row.slotId}`,
      meta: { hoursUntil, serviceType: row.serviceType },
    },
  };
}
